// ── payrollLedgerSync.js — 급여 → 거래장부 자동 입력 ─────────────
// 월별 급여 계산 결과를 거래(transaction)로 일괄 생성.
// sourceReceiptId 패턴으로 중복 방지 (같은 ID 있으면 update, 없으면 add).
//
// 거래 구조:
//   - 직원별 1건 (총급여 = totalEarnings)
//   - category: { major: 'labor', sub: 'salary' }
//   - 사업주부담 4대보험 옵션 시 별도 1건 (sub: 'social_insurance')
//   - sourceReceiptId: `payroll-{monthKey}-{empId}` 또는 `payroll-{monthKey}-employer-insurance`
//   - source: 'payroll' (TXN_SOURCES에 추가)
//   - evidence: paper_receipt (통장 이체 — 비적격증빙)

import {
  collection, doc, addDoc, setDoc, query, where, getDocs, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../db';

/**
 * 한 명의 급여를 거래로 변환
 */
function payrollToTransaction(payroll, monthKey, employee, payDay = 25) {
  const [y, m] = monthKey.split('-').map(Number);
  // 지급일이 그 달 말일 초과면 말일로 보정
  const lastDayOfMonth = new Date(y, m, 0).getDate();
  const day = Math.min(payDay, lastDayOfMonth);
  const dateStr = `${monthKey}-${String(day).padStart(2, '0')}`;
  const total = Number(payroll.totalEarnings) || 0;

  return {
    type: 'expense',
    date: dateStr,
    supply: total,                  // 인건비는 부가세 없음
    vat: 0,
    total,
    partnerId: null,
    partnerName: employee?.name || '직원',
    evidence: {
      type: 'paper_receipt',        // 통장이체 — 비적격증빙
      isQualified: false,
      sourceUrl: null,
      externalId: null,
    },
    category: { major: 'labor', sub: 'salary' },
    revenueCategory: null,
    itemName: `${y}년 ${m}월 ${employee?.name || ''} 급여`,
    memo: `자동 입력 (인건비 모듈) · 근무 ${payroll.totalHours}h`,
    vatTreatment: {
      deductible: false,            // 인건비는 매입세액공제 X
      presumed: false,
      presumedRate: null,
      nonDeductibleReason: '인건비',
    },
    incomeTreatment: {
      isBusinessExpense: true,
      isPersonal: false,
      accountCode: null,
    },
    projectId: null,
    recipeId: null,
    menuId: null,
    sourceReceiptId: `payroll-${monthKey}-${employee?.id || 'unknown'}`,
    splitInfo: null,
    source: 'payroll',
    needsReview: false,
    confidence: null,
  };
}

/**
 * 사업주부담 4대보험을 거래로 변환 (월별 1건)
 */
function employerInsuranceToTransaction(monthKey, employerInsurance, payDay = 25) {
  if (!employerInsurance || employerInsurance <= 0) return null;
  const [y, m] = monthKey.split('-').map(Number);
  const lastDayOfMonth = new Date(y, m, 0).getDate();
  const day = Math.min(payDay, lastDayOfMonth);
  const dateStr = `${monthKey}-${String(day).padStart(2, '0')}`;
  return {
    type: 'expense',
    date: dateStr,
    supply: employerInsurance,
    vat: 0,
    total: employerInsurance,
    partnerId: null,
    partnerName: '국민연금공단·건강보험공단·근로복지공단',
    evidence: {
      type: 'paper_receipt',
      isQualified: false,           // 4대보험 영수증은 별도 처리 (실제로는 적격이지만 자동입력은 보수적으로)
      sourceUrl: null,
      externalId: null,
    },
    category: { major: 'labor', sub: 'social_insurance' },
    revenueCategory: null,
    itemName: `${y}년 ${m}월 4대보험 (사업주부담)`,
    memo: '자동 입력 (인건비 모듈)',
    vatTreatment: {
      deductible: false,
      presumed: false,
      presumedRate: null,
      nonDeductibleReason: '4대보험',
    },
    incomeTreatment: {
      isBusinessExpense: true,
      isPersonal: false,
      accountCode: null,
    },
    projectId: null,
    recipeId: null,
    menuId: null,
    sourceReceiptId: `payroll-${monthKey}-employer-insurance`,
    splitInfo: null,
    source: 'payroll',
    needsReview: false,
    confidence: null,
  };
}

/**
 * 기존 payroll 거래 조회 (중복 체크용)
 * sourceReceiptId가 'payroll-{monthKey}-' 로 시작하는 거래
 */
async function fetchExistingPayrollTxns(uid, monthKey) {
  const ref = collection(db, 'users', uid, 'transactions');
  // Firestore에 startsWith 가 없으니 모든 payroll 거래 가져와서 client filter
  // (월별 한정이라 양 적음)
  const q = query(ref, where('source', '==', 'payroll'));
  const snap = await getDocs(q);
  const result = new Map();  // sourceReceiptId → docId
  snap.forEach(d => {
    const data = d.data();
    if (data.sourceReceiptId?.startsWith(`payroll-${monthKey}-`)) {
      result.set(data.sourceReceiptId, d.id);
    }
  });
  return result;
}

/**
 * 메인: 월급여 일괄 거래장부 입력
 * @param {string} uid — 사용자 UID
 * @param {string} monthKey — 'YYYY-MM'
 * @param {Array} payrolls — calcMonthlyPayroll 결과 배열
 * @param {Array} employees — 직원 목록 (이름·ID 매핑)
 * @param {number} employerInsurance — 사업주부담 4대보험 합계 (옵션)
 * @param {object} settings — payrollSettings (payDay 등)
 * @returns {Promise<{added, updated, skipped, errors}>}
 */
export async function syncPayrollToLedger(uid, monthKey, payrolls, employees, employerInsurance = 0, settings = {}) {
  if (!uid) throw new Error('사용자 인증 필요');
  if (!monthKey) throw new Error('monthKey 필수');
  if (!Array.isArray(payrolls)) throw new Error('payrolls 배열 필요');

  const payDay = Number(settings.payDay) || 25;
  const existing = await fetchExistingPayrollTxns(uid, monthKey);
  const ref = collection(db, 'users', uid, 'transactions');

  let added = 0, updated = 0, skipped = 0;
  const errors = [];

  // 1) 직원별 급여
  for (const p of payrolls) {
    const emp = employees.find(e => e.id === p.employeeId);
    if (!emp) { skipped++; continue; }
    if ((Number(p.totalEarnings) || 0) <= 0) { skipped++; continue; }
    const txn = payrollToTransaction(p, monthKey, emp, payDay);
    const existingDocId = existing.get(txn.sourceReceiptId);
    try {
      if (existingDocId) {
        // 업데이트 (금액 변경됐을 수 있음)
        await setDoc(
          doc(db, 'users', uid, 'transactions', existingDocId),
          { ...txn, updatedAt: serverTimestamp() },
          { merge: true }
        );
        updated++;
      } else {
        await addDoc(ref, { ...txn, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
        added++;
      }
    } catch (e) {
      console.error('payroll txn error:', e);
      errors.push({ employee: emp.name, error: String(e) });
    }
  }

  // 2) 사업주부담 4대보험 (옵션)
  if (employerInsurance && employerInsurance > 0) {
    const txn = employerInsuranceToTransaction(monthKey, employerInsurance, payDay);
    if (txn) {
      const existingDocId = existing.get(txn.sourceReceiptId);
      try {
        if (existingDocId) {
          await setDoc(
            doc(db, 'users', uid, 'transactions', existingDocId),
            { ...txn, updatedAt: serverTimestamp() },
            { merge: true }
          );
          updated++;
        } else {
          await addDoc(ref, { ...txn, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
          added++;
        }
      } catch (e) {
        console.error('employer insurance txn error:', e);
        errors.push({ kind: '사업주부담 4대보험', error: String(e) });
      }
    }
  }

  return { added, updated, skipped, errors };
}
